import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Odpoved, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';
import OdpovedKarta from './OdpovedKarta';
import { useNestihaVyjezd } from '../hooks/useNestihaVyjezd';
import type { ZalohaJinde } from '../api/JsdhNotifikaceService/generated/models/ZalohaJinde';
import { BEZ_ZALOHY, rozdelOdpovedi } from '../utils/rozdelOdpovedi';
import { ZalohaSkupina } from './ZalohaSkupina';
import { ZalohaPilulka } from './ZalohaPilulka';
import { COUNTER_PILULKA, COUNTER_VYSKA, spocitejKapacituKaret, spocitejSloupce } from '../utils/kartyKapacita';

interface OdpovediHasicuProps {
    odpovedi: Odpoved[];
    /** Lide v zaloze u jineho aktivniho poplachu (GET .../zaloha-jinde). */
    zalohaJinde?: ZalohaJinde[];
    test?: boolean;
    showSpecializace?: boolean;
    displejStyl?: DisplejStylOdpovedi;
    /** ISO cas vyhlaseni poplachu — predava se kartam kvuli odpoctu dojezdu. */
    casVyhlaseni?: string;
    /** Vyska (px), do ktere se karty maji vejit; nad ni se misto useknuti ukaze „+ X dalsich“. 0/undefined = bez limitu. */
    dostupnaVyska?: number;
    /** @deprecated Po redesignu (2026-04-29) se ignoruje — layout je vždy 2-col split podle displejStyl. Prop zůstává pro zpětnou kompatibilitu volajících. */
    adaptivniLayout?: boolean;
}

const generateTestOdpovedi = (): Odpoved[] => {
    const jmena = [
        'Jan Novák', 'Petr Svoboda', 'Martin Dvořák', 'Tomáš Černý', 'Josef Procházka',
        'Pavel Kučera', 'Jaroslav Veselý', 'Miroslav Horák', 'Zdeněk Němec', 'František Marek',
        'Václav Pospíšil', 'Karel Hájek', 'Milan Jelínek', 'Jiří Král', 'Lukáš Růžička',
        'David Beneš', 'Jakub Fiala', 'Ondřej Sedláček', 'Radek Doležal', 'Michal Urban',
        'Adam Kovář', 'Marek Kratochvíl', 'Stanislav Blažek', 'Roman Kolář', 'Filip Navrátil',
        'Vojtěch Zeman', 'Aleš Štěpánek', 'Daniel Holub', 'Patrik Musil', 'Matěj Pokorný'
    ];
    const now = new Date().toISOString();
    return jmena.map((jmeno, index): Odpoved => {
        const isJedu = index < 20;
        const funkceKod = index < 2 ? 'VD' : index < 6 ? 'S' : 'H';
        const funkceNazev = funkceKod === 'VD' ? 'Velitel družstva' : funkceKod === 'S' ? 'Strojník' : 'Hasič';
        const specializace = index === 0 ? [{ id: 1, kod: 'NDT', nazev: 'Nositel dýchací techniky' }]
            : index === 2 ? [{ id: 2, kod: 'PILAR', nazev: 'Obsluhovatel motorové pily' }]
            : index === 3 ? [{ id: 1, kod: 'NDT', nazev: 'Nositel dýchací techniky' }, { id: 2, kod: 'PILAR', nazev: 'Obsluhovatel motorové pily' }]
            : [];
        return {
            id: index + 1,
            poplachId: 1,
            uzivatelId: index + 1,
            jmeno,
            odpoved: isJedu ? 'JEDU' : 'NEJEDU',
            cas: now,
            funkce: [{ id: index + 1, kod: funkceKod, nazev: funkceNazev }],
            funkceAsString: funkceNazev,
            specializace,
        };
    });
};

const counterColor = (jedu: boolean): string => (jedu ? '#2e7d32' : '#c62828');

const CounterChip: React.FC<{ jedu: boolean; count: number; pocetZaloha?: number }> = ({ jedu, count, pocetZaloha = 0 }) => (
    <Box
        data-counter={jedu ? 'JEDU' : 'NEJEDU'}
        sx={{
            backgroundColor: counterColor(jedu),
            color: '#fff',
            borderRadius: 'var(--dashboard-counter-radius, 8px)',
            padding: 'var(--dashboard-counter-padding, 12px 8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            minWidth: 'var(--dashboard-counter-width, 96px)',
            width: 'var(--dashboard-counter-width, 96px)',
            alignSelf: 'stretch',
        }}
    >
        <Box
            component="span"
            sx={{
                fontSize: 'var(--dashboard-counter-label-size, 1rem)',
                letterSpacing: '1px',
                opacity: 0.95,
                lineHeight: 1,
            }}
        >
            {jedu ? 'JEDU' : 'NEJEDU'}
        </Box>
        <Box
            component="span"
            sx={{
                fontSize: 'var(--dashboard-counter-num-size, 3.5rem)',
                lineHeight: 1,
                marginTop: 0.5,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {count}
        </Box>
        {jedu && <ZalohaPilulka pocet={pocetZaloha} />}
    </Box>
);

const PlusDalsich: React.FC<{ pocet: number }> = ({ pocet }) => (
    <Typography
        data-testid="odpovedi-plus-dalsich"
        sx={{ gridColumn: '1 / -1', fontSize: '1rem', fontWeight: 700, color: 'text.secondary', pl: 1 }}
    >
        + {pocet} dalších
    </Typography>
);

/** Zmeri skutecny radek karet (vyska karty + mezera) a pocet sloupcu mrizky; v jsdom padne na odhad. */
const useMeritKarty = (
    refs: Array<React.RefObject<HTMLDivElement | null>>,
    odhadKarty: number,
    mezera: number,
    odhadSloupcu: number,
    klic: string,
) => {
    const [mereni, setMereni] = useState({ radekH: odhadKarty + mezera, sloupcu: odhadSloupcu });
    useLayoutEffect(() => {
        const zmer = () => {
            const el = refs.map((r) => r.current).find((e) => e && e.firstElementChild);
            if (!el) return;
            const h = (el.firstElementChild as HTMLElement).getBoundingClientRect().height;
            const sloupcu = spocitejSloupce(getComputedStyle(el).gridTemplateColumns) || odhadSloupcu;
            const radekH = (h > 0 ? h : odhadKarty) + mezera;
            setMereni((p) => (p.radekH === radekH && p.sloupcu === sloupcu ? p : { radekH, sloupcu }));
        };
        zmer();
        // Prepocet pri zmene sirky/kontejneru a po nacteni webfontu; bez timeru (RPi).
        let zrusene = false;
        const RO = typeof ResizeObserver !== 'undefined' ? ResizeObserver : undefined;
        const ro = RO ? new RO(() => zmer()) : undefined;
        if (ro) refs.forEach((r) => { if (r.current) ro.observe(r.current); });
        const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
        fonts?.ready?.then(() => { if (!zrusene) zmer(); });
        return () => {
            zrusene = true;
            ro?.disconnect();
        };
    }, [refs, odhadKarty, mezera, odhadSloupcu, klic]);
    return mereni;
};

interface SectionWithCounterProps {
    jedu: boolean;
    odpovedi: Odpoved[];
    showSpecializace: boolean;
    casVyhlaseni?: string;
    jeNestihajici: (o: Odpoved) => boolean;
    /** Kolik karet se ukaze (zbytek nahradi „+ X dalsich“). */
    maxKaret?: number;
    gridRef?: React.Ref<HTMLDivElement>;
    pocetZaloha?: number;
}

const SectionWithCounter: React.FC<SectionWithCounterProps> = ({
    jedu, odpovedi, showSpecializace, casVyhlaseni, jeNestihajici, maxKaret = Number.MAX_SAFE_INTEGER, gridRef, pocetZaloha = 0,
}) => {
    const viditelne = odpovedi.length > maxKaret ? odpovedi.slice(0, maxKaret) : odpovedi;
    const counter = <CounterChip jedu={jedu} count={odpovedi.length} pocetZaloha={pocetZaloha} />;
    const grid = (
        <Box
            ref={gridRef}
            data-section={jedu ? 'JEDU' : 'NEJEDU'}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.75,
                alignContent: 'start',
                flex: 1,
                minWidth: 0,
            }}
        >
            {viditelne.map((o) => (
                <OdpovedKarta
                    key={o.id ?? o.uzivatelId}
                    odpoved={o}
                    showSpecializace={showSpecializace}
                    displayStyle="BACKGROUND_TINTED"
                    tintCard={true}
                    largeMode={false}
                    casVyhlaseni={casVyhlaseni}
                    nestiha={jeNestihajici(o)}
                />
            ))}
            {odpovedi.length > viditelne.length && <PlusDalsich pocet={odpovedi.length - viditelne.length} />}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', minWidth: 0 }}>
            {jedu ? counter : grid}
            {jedu ? grid : counter}
        </Box>
    );
};

interface StripeSectionProps {
    jedu: boolean;
    odpovedi: Odpoved[];
    showSpecializace: boolean;
    casVyhlaseni?: string;
    jeNestihajici: (o: Odpoved) => boolean;
    maxKaret?: number;
    gridRef?: React.Ref<HTMLDivElement>;
}

const StripeSection: React.FC<StripeSectionProps> = ({
    jedu, odpovedi, showSpecializace, casVyhlaseni, jeNestihajici, maxKaret = Number.MAX_SAFE_INTEGER, gridRef,
}) => {
    const viditelne = odpovedi.length > maxKaret ? odpovedi.slice(0, maxKaret) : odpovedi;
    return (
    <Box
        ref={gridRef}
        data-section={jedu ? 'JEDU' : 'NEJEDU'}
        sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 0.5,
            alignContent: 'start',
            minWidth: 0,
        }}
    >
        {viditelne.map((o) => (
            <OdpovedKarta
                key={o.id ?? o.uzivatelId}
                odpoved={o}
                showSpecializace={showSpecializace}
                displayStyle="STRIPE"
                largeMode={false}
                casVyhlaseni={casVyhlaseni}
                nestiha={jeNestihajici(o)}
            />
        ))}
        {odpovedi.length > viditelne.length && <PlusDalsich pocet={odpovedi.length - viditelne.length} />}
    </Box>
    );
};

const OdpovediHasicu: React.FC<OdpovediHasicuProps> = ({
    odpovedi,
    zalohaJinde = BEZ_ZALOHY,
    test = false,
    showSpecializace = false,
    displejStyl = 'STRIPE',
    casVyhlaseni,
    dostupnaVyska = 0,
}) => {
    const data = useMemo(() => (test ? generateTestOdpovedi() : odpovedi), [test, odpovedi]);
    const rozdeleni = useMemo(() => rozdelOdpovedi(data, zalohaJinde), [data, zalohaJinde]);
    // Jeden observer na celou obrazovku, ne na kazdou kartu (RPi). Jen jedouci — zaloha je ve zbrojnici.
    const jeNestihajici = useNestihaVyjezd(casVyhlaseni, rozdeleni.jedou);
    const jedouOdpovedi = rozdeleni.jedou;
    const nejedouOdpovedi = rozdeleni.nejedou;
    const pocetZaloha = rozdeleni.zaloha.length;

    const tinted = displejStyl === 'BACKGROUND_TINTED';
    const jeduRef = useRef<HTMLDivElement>(null);
    const nejeduRef = useRef<HTMLDivElement>(null);
    const refs = useMemo(() => [jeduRef, nejeduRef], []);
    // Odhad (kdyz se nic nenamerilo): STRIPE karta ~ 37 px, mezera 4; tinted 60 px, mezera 6, 2 sloupce.
    const { radekH, sloupcu } = useMeritKarty(refs, tinted ? 60 : 37, tinted ? 6 : 4, tinted ? 2 : 1,
        `${jedouOdpovedi.length}|${nejedouOdpovedi.length}|${displejStyl}`);
    const kapacita = spocitejKapacituKaret({
        vyska: dostupnaVyska, radekH, sloupcu,
        pocetJedou: jedouOdpovedi.length, pocetNejedou: nejedouOdpovedi.length, pocetZaloha,
        // CounterChip drzi sloupec Jedou na jeho vysce (jen BACKGROUND_TINTED).
        counterH: tinted ? COUNTER_VYSKA + (pocetZaloha > 0 ? COUNTER_PILULKA : 0) : 0,
    });

    if (displejStyl === 'BACKGROUND_TINTED') {
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    pt: 1,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <SectionWithCounter
                        jedu={true}
                        odpovedi={jedouOdpovedi}
                        showSpecializace={showSpecializace}
                        casVyhlaseni={casVyhlaseni}
                        jeNestihajici={jeNestihajici}
                        maxKaret={kapacita.maxJedou}
                        gridRef={jeduRef}
                        pocetZaloha={pocetZaloha}
                    />
                    <ZalohaSkupina polozky={rozdeleni.zaloha} fontSize="1.1rem" maxViditelnych={kapacita.maxZaloha} />
                </Box>
                <SectionWithCounter
                    jedu={false}
                    odpovedi={nejedouOdpovedi}
                    showSpecializace={showSpecializace}
                    casVyhlaseni={casVyhlaseni}
                    jeNestihajici={jeNestihajici}
                    maxKaret={kapacita.maxNejedou}
                    gridRef={nejeduRef}
                />
            </Box>
        );
    }

    // STRIPE styl - 2-col split JEDU/NEJEDU bez counter chipu (kompaktni karty s vlastnim JEDU/NEJEDU chipem)
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                pt: 1,
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <StripeSection jedu={true} odpovedi={jedouOdpovedi} showSpecializace={showSpecializace} casVyhlaseni={casVyhlaseni} jeNestihajici={jeNestihajici} maxKaret={kapacita.maxJedou} gridRef={jeduRef} />
                <ZalohaSkupina polozky={rozdeleni.zaloha} fontSize="1.1rem" maxViditelnych={kapacita.maxZaloha} />
            </Box>
            <StripeSection jedu={false} odpovedi={nejedouOdpovedi} showSpecializace={showSpecializace} casVyhlaseni={casVyhlaseni} jeNestihajici={jeNestihajici} maxKaret={kapacita.maxNejedou} gridRef={nejeduRef} />
        </Box>
    );
};

export default React.memo(OdpovediHasicu);
